import { useState, useEffect, useRef, useCallback } from 'react';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { acquireApiToken } from '@/auth/acquireApiToken';

export interface BlogPost {
  id: string;
  title: string;
  subtitle: string;
  slug: string;
  body: string;
  author: string;
  imageUrl: string;
  status: 'draft' | 'published' | string;
  createdAt: string;
  updatedAt: string;
}

interface BlogPostsResponse {
  items: BlogPost[];
  count: number;
  nextToken: string | null;
}

interface UseBlogPostsResult {
  posts: BlogPost[];
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

export function useBlogPosts(status?: string, apiUrl?: string): UseBlogPostsResult {
  const isAuthenticated = useIsAuthenticated();
  const { instance, accounts } = useMsal();
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const accountsRef = useRef(accounts);
  accountsRef.current = accounts;
  const [refreshTick, setRefreshTick] = useState(0);

  const refetch = useCallback(() => setRefreshTick(t => t + 1), []);

  useEffect(() => {
    let isMounted = true;

    const fetchPosts = async () => {
      const resolvedApiUrl = apiUrl || process.env.REACT_APP_API_URL;
      if (!resolvedApiUrl) {
        if (isMounted) { setError('REACT_APP_API_URL is not set'); setLoading(false); }
        return;
      }
      if (isMounted) setLoading(true);
      try {
        let token: string | null = null;
        if (isAuthenticated && accountsRef.current.length > 0) {
          token = await acquireApiToken(instance, accountsRef.current[0]);
        }
        const params = new URLSearchParams({ limit: '100' });
        if (status) params.set('status', status);

        const response = await fetch(`${resolvedApiUrl}/admin/blog-posts?${params}`, {
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        });
        const data: BlogPostsResponse = await response.json();
        if (!response.ok) throw new Error((data as any).error || `HTTP ${response.status}`);
        if (isMounted) { setPosts(data.items); setError(null); }
      } catch (err: any) {
        if (isMounted) { setError(err?.message ?? 'Unknown error'); setPosts([]); }
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchPosts();
    return () => { isMounted = false; };
  }, [isAuthenticated, instance, status, refreshTick, apiUrl]);

  return { posts, loading, error, refetch };
}

// ── Mutations ────────────────────────────────────────────────────────────────

function useAdminToken() {
  const isAuthenticated = useIsAuthenticated();
  const { instance, accounts } = useMsal();
  const accountsRef = useRef(accounts);
  accountsRef.current = accounts;

  return useCallback(async (): Promise<string | null> => {
    if (!isAuthenticated || accountsRef.current.length === 0) return null;
    try {
      const resp = { accessToken: await acquireApiToken(instance, accountsRef.current[0]) };
      return resp.accessToken;
    } catch { return null; }
  }, [isAuthenticated, instance]);
}

export type BlogPostInput = Pick<BlogPost, 'title' | 'body'> &
  Partial<Pick<BlogPost, 'subtitle' | 'slug' | 'author' | 'imageUrl' | 'status'>>;

interface MutationResult {
  loading: boolean;
  error: string | null;
}

export function useCreateBlogPost(apiUrl?: string): MutationResult & { create: (input: BlogPostInput) => Promise<BlogPost | null> } {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const getToken = useAdminToken();

  const create = useCallback(async (input: BlogPostInput): Promise<BlogPost | null> => {
    const resolvedApiUrl = apiUrl || process.env.REACT_APP_API_URL;
    if (!resolvedApiUrl) { setError('REACT_APP_API_URL is not set'); return null; }
    setLoading(true); setError(null);
    try {
      const token = await getToken();
      const response = await fetch(`${resolvedApiUrl}/admin/blog-posts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify(input),
      });
      const data = await response.json();
      if (!response.ok) { setError(data.error || `HTTP ${response.status}`); return null; }
      return data as BlogPost;
    } catch (err: any) {
      setError(err?.message ?? 'Unknown error'); return null;
    } finally { setLoading(false); }
  }, [getToken, apiUrl]);

  return { loading, error, create };
}

export function useUpdateBlogPost(apiUrl?: string): MutationResult & { update: (id: string, input: Partial<BlogPostInput>) => Promise<boolean> } {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const getToken = useAdminToken();

  const update = useCallback(async (id: string, input: Partial<BlogPostInput>): Promise<boolean> => {
    const resolvedApiUrl = apiUrl || process.env.REACT_APP_API_URL;
    if (!resolvedApiUrl) { setError('REACT_APP_API_URL is not set'); return false; }
    setLoading(true); setError(null);
    try {
      const token = await getToken();
      const response = await fetch(`${resolvedApiUrl}/admin/blog-posts/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify(input),
      });
      const data = await response.json();
      if (!response.ok) { setError(data.error || `HTTP ${response.status}`); return false; }
      return true;
    } catch (err: any) {
      setError(err?.message ?? 'Unknown error'); return false;
    } finally { setLoading(false); }
  }, [getToken, apiUrl]);

  return { loading, error, update };
}

export function useUploadBlogPostImage(apiUrl?: string): MutationResult & { upload: (file: File) => Promise<string | null> } {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const getToken = useAdminToken();

  const upload = useCallback(async (file: File): Promise<string | null> => {
    const resolvedApiUrl = apiUrl || process.env.REACT_APP_API_URL;
    if (!resolvedApiUrl) { setError('REACT_APP_API_URL is not set'); return null; }

    const MAX_BYTES = 5 * 1024 * 1024;
    const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

    if (!ALLOWED_TYPES.includes(file.type)) {
      setError('Unsupported file type. Please use JPEG, PNG, or WebP.');
      return null;
    }
    if (file.size > MAX_BYTES) {
      setError(`Image is too large (${(file.size / 1024 / 1024).toFixed(1)} MB). Maximum size is 5 MB.`);
      return null;
    }

    setLoading(true); setError(null);
    try {
      const token = await getToken();

      // Step 1: Get presigned URL
      const signingResp = await fetch(`${resolvedApiUrl}/admin/blog-posts/image`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ contentType: file.type }),
      });
      const signingData = await signingResp.json();
      if (!signingResp.ok) { setError(signingData.message || 'Failed to get upload URL'); return null; }

      // Step 2: Upload to S3
      const formData = new FormData();
      Object.entries(signingData.upload.fields).forEach(([key, value]) => {
        formData.append(key, value as string);
      });
      formData.append('file', file);

      const uploadResp = await fetch(signingData.upload.url, {
        method: 'POST',
        body: formData,
      });
      if (!uploadResp.ok && uploadResp.status !== 204) {
        setError('Failed to upload image. Please check the file and try again.');
        return null;
      }

      return signingData.cdn_url;
    } catch (err: any) {
      setError(err?.message ?? 'Unknown error'); return null;
    } finally { setLoading(false); }
  }, [getToken, apiUrl]);

  return { loading, error, upload };
}

export function useDeleteBlogPost(apiUrl?: string): MutationResult & { remove: (id: string) => Promise<boolean> } {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const getToken = useAdminToken();

  const remove = useCallback(async (id: string): Promise<boolean> => {
    const resolvedApiUrl = apiUrl || process.env.REACT_APP_API_URL;
    if (!resolvedApiUrl) { setError('REACT_APP_API_URL is not set'); return false; }
    setLoading(true); setError(null);
    try {
      const token = await getToken();
      const response = await fetch(`${resolvedApiUrl}/admin/blog-posts/${id}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      });
      if (!response.ok) { const data = await response.json(); setError(data.error || `HTTP ${response.status}`); return false; }
      return true;
    } catch (err: any) {
      setError(err?.message ?? 'Unknown error'); return false;
    } finally { setLoading(false); }
  }, [getToken, apiUrl]);

  return { loading, error, remove };
}
